import React, { useState, useMemo } from 'react';
import { API_URL } from '../config';

/**
 * BulkContactImport – hromadné doplňování kontaktů ke společnostem podle IČO.
 *
 * Workflow:
 *  1. Uživatel vloží seznam IČO (jeden na řádek) → klikne "Vyhledat firmy"
 *  2. Zobrazí se náhled: která IČO byla nalezena, která ne
 *  3. Uživatel vloží kontaktní jména / emaily / tel. čísla (jeden na řádek, ve stejném pořadí jako IČO)
 *  4. Klikne "Importovat kontakty" → kontakty se přidají ke společnostem
 */
export default function BulkContactImport({ onClose, isDarkMode, onImportComplete }) {
  // ── Krok 1: seznam IČO ──────────────────────────────────────────────────
  const [icoInput, setIcoInput] = useState('');

  // ── Výsledky vyhledávání ────────────────────────────────────────────────
  const [lookupResults, setLookupResults] = useState(null); // null = ještě neprovedeno
  const [isLooking, setIsLooking] = useState(false);

  // ── Krok 2: kontaktní data ──────────────────────────────────────────────
  const [namesInput, setNamesInput] = useState('');
  const [emailsInput, setEmailsInput] = useState('');
  const [phonesInput, setPhonesInput] = useState('');

  // ── Import výsledky ─────────────────────────────────────────────────────
  const [importResults, setImportResults] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');

  // ── Pomocné ─────────────────────────────────────────────────────────────
  const splitLines = (text) =>
    text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

  const icoLines = splitLines(icoInput);

  // ────────────────────────────────────────────────────────────────────────
  // Krok 1 – Vyhledat firmy
  // ────────────────────────────────────────────────────────────────────────
  const handleLookup = async () => {
    if (icoLines.length === 0) return;
    setIsLooking(true);
    setLookupResults(null);
    setImportResults(null);
    setImportError('');
    try {
      const response = await fetch(`${API_URL}/companies/lookup-by-ico-with-contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ icos: icoLines }),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(`HTTP ${response.status}: ${err}`);
      }
      const data = await response.json();
      setLookupResults(data);

      // Předvyplň prázdné řádky pro kontaktní pole (pro každé IČO jeden)
      const emptyLines = data.map(() => '').join('\n');
      if (!namesInput) setNamesInput(emptyLines);
      if (!emailsInput) setEmailsInput(emptyLines);
      if (!phonesInput) setPhonesInput(emptyLines);
    } catch (e) {
      setImportError(`Chyba při vyhledávání: ${e.message}`);
    } finally {
      setIsLooking(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Krok 2 – Importovat kontakty
  // ────────────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!lookupResults) return;

    // Poziciální mapování – prázdné řádky zachovávají pořadí
    const byLine = (text) => text.split('\n');
    const names  = byLine(namesInput);
    const emails = byLine(emailsInput);
    const phones = byLine(phonesInput);

    // Sestavení entries jen pro řádky kde firma nalezena A jsou data
    const entries = lookupResults
      .map((row, idx) => {
        if (!row.found) return null;
        const name  = (names[idx]  || '').trim();
        const email = (emails[idx] || '').trim();
        const phone = (phones[idx] || '').trim();
        if (!name && !email && !phone) return null;
        return { ico: row.ico, name, email, phone };
      })
      .filter(Boolean);

    if (entries.length === 0) {
      setImportError('Žádné kontaktní údaje k importu. Vyplňte alespoň jméno, email nebo telefon.');
      return;
    }

    setIsImporting(true);
    setImportError('');
    try {
      const response = await fetch(`${API_URL}/companies/bulk-add-contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ entries }),
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(`HTTP ${response.status}: ${err}`);
      }
      const data = await response.json();
      setImportResults(data);
      if (onImportComplete) onImportComplete();
    } catch (e) {
      setImportError(`Chyba při importu: ${e.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Styly
  // ────────────────────────────────────────────────────────────────────────
  const card = isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const overlay = 'fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4';
  const label = `text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const textarea = `w-full px-3 py-2 text-sm border rounded-lg font-mono resize-y ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
  } focus:outline-none focus:ring-1 focus:ring-blue-500`;

  const foundCount = lookupResults ? lookupResults.filter((r) => r.found).length : 0;
  const notFoundCount = lookupResults ? lookupResults.filter((r) => !r.found).length : 0;

  // ── Kontrolní náhled – živě přepočítávaný z textarea polí ──────────────
  // Zachovává poziční mapování (prázdný řádek = žádný kontakt pro to IČO)
  const previewRows = useMemo(() => {
    if (!lookupResults) return [];
    // Rozdělení po řádcích bez ořezávání prázdných (zachování pozice)
    const byLine = (text) => text.split('\n');
    const names  = byLine(namesInput);
    const emails = byLine(emailsInput);
    const phones = byLine(phonesInput);
    return lookupResults.map((row, idx) => {
      const name  = (names[idx]  || '').trim();
      const email = (emails[idx] || '').trim();
      const phone = (phones[idx] || '').trim();
      const hasData = name || email || phone;
      let action = 'skip';
      if (!row.found) action = 'notFound';
      else if (hasData) action = 'add';
      return { ...row, idx, newName: name, newEmail: email, newPhone: phone, action };
    });
  }, [lookupResults, namesInput, emailsInput, phonesInput]);

  const previewAddCount  = previewRows.filter((r) => r.action === 'add').length;
  const previewSkipCount = previewRows.filter((r) => r.action === 'skip').length;

  return (
    <div className={overlay}>
      <div
        className={`${card} max-w-5xl w-full max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div>
            <h2 className="text-xl font-bold">📋 Hromadné doplnění kontaktů</h2>
            <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Vložte seznam IČO, vyhledejte firmy a doplňte kontaktní osoby ve stejném pořadí.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl w-8 h-8 flex items-center justify-center rounded-full transition"
            title="Zavřít"
          >
            ✕
          </button>
        </div>

        {/* Body – scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* ── Krok 1: IČO ─────────────────────────────────────────────── */}
          <section>
            <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
              Zadejte IČO (jeden na řádek)
            </h3>
            <div className="flex gap-3">
              <div className="flex-1">
                <textarea
                  className={textarea}
                  rows={8}
                  placeholder={'12345678\n87654321\n11223344'}
                  value={icoInput}
                  onChange={(e) => {
                    setIcoInput(e.target.value);
                    setLookupResults(null);
                    setImportResults(null);
                  }}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Celkem řádků: {icoLines.length}
                </p>
              </div>
              <div className="flex flex-col justify-start gap-2 pt-0.5">
                <button
                  onClick={handleLookup}
                  disabled={icoLines.length === 0 || isLooking}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-sm transition whitespace-nowrap"
                >
                  {isLooking ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Vyhledávám…
                    </span>
                  ) : (
                    '🔍 Vyhledat firmy'
                  )}
                </button>
                {lookupResults && (
                  <div className="text-xs space-y-0.5">
                    <p className="text-green-500 font-medium">✅ Nalezeno: {foundCount}</p>
                    {notFoundCount > 0 && (
                      <p className="text-red-400 font-medium">❌ Nenalezeno: {notFoundCount}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* ── Náhled nalezených firem ─────────────────────────────────── */}
          {lookupResults && (
            <section>
              <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                <span className="bg-gray-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">↓</span>
                Nalezené firmy
              </h3>
              <div
                className={`rounded-lg border overflow-hidden text-sm ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-200'
                }`}
              >
                <table className="w-full">
                  <thead>
                    <tr
                      className={`text-xs uppercase ${
                        isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                      }`}
                    >
                      <th className="px-3 py-2 text-left w-8">#</th>
                      <th className="px-3 py-2 text-left">IČO</th>
                      <th className="px-3 py-2 text-left">Název firmy</th>
                      <th className="px-3 py-2 text-left">Stávající kontakty</th>
                      <th className="px-3 py-2 text-left">Stav</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lookupResults.map((row, idx) => (
                      <tr
                        key={idx}
                        className={`border-t ${
                          isDarkMode ? 'border-gray-700' : 'border-gray-100'
                        } ${row.found ? '' : isDarkMode ? 'opacity-50' : 'opacity-60'}`}
                      >
                        <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2 font-mono">{row.ico}</td>
                        <td className="px-3 py-2 font-medium">
                          {row.found ? row.companyName : <span className="italic text-gray-400">–</span>}
                        </td>
                        <td className="px-3 py-2">
                          {row.found && row.existingContacts.length > 0 ? (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {row.existingContacts
                                .map((c) => [c.name, c.phone, c.email].filter(Boolean).join(' | '))
                                .join('; ')}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">žádné</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {row.found ? (
                            <span className="text-green-500 font-medium text-xs">✅ Nalezena</span>
                          ) : (
                            <span className="text-red-400 font-medium text-xs">❌ Nenalezena</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Krok 2: kontaktní data ───────────────────────────────────── */}
          {lookupResults && !importResults && (
            <section>
              <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                Kontaktní údaje ve stejném pořadí jako IČO
              </h3>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Každý řádek odpovídá jednomu IČO. Pro nenalezené firmy nechte řádek prázdný.
                Musí být vyplněno alespoň jedno pole (jméno, email nebo telefon).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className={label}>👤 Jméno kontaktní osoby</p>
                  <textarea
                    className={textarea}
                    rows={lookupResults.length + 1}
                    placeholder={lookupResults.map((_, i) => `Osoba ${i + 1}`).join('\n')}
                    value={namesInput}
                    onChange={(e) => setNamesInput(e.target.value)}
                  />
                </div>
                <div>
                  <p className={label}>📧 Email</p>
                  <textarea
                    className={textarea}
                    rows={lookupResults.length + 1}
                    placeholder={lookupResults.map((_, i) => `email${i + 1}@firma.cz`).join('\n')}
                    value={emailsInput}
                    onChange={(e) => setEmailsInput(e.target.value)}
                  />
                </div>
                <div>
                  <p className={label}>📞 Telefonní číslo</p>
                  <textarea
                    className={textarea}
                    rows={lookupResults.length + 1}
                    placeholder={lookupResults.map((_, i) => `+420 xxx xxx xx${i}`).join('\n')}
                    value={phonesInput}
                    onChange={(e) => setPhonesInput(e.target.value)}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ── Krok 3: Kontrolní náhled před importem ──────────────────── */}
          {lookupResults && !importResults && previewRows.length > 0 && (
            <section>
              <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                <span className="bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                Kontrolní náhled před importem
                <span className={`ml-auto text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Zkontrolujte co se importuje
                </span>
              </h3>

              {/* Mini souhrn */}
              <div className="flex gap-3 mb-3 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700">
                  ✅ Bude přidáno: {previewAddCount}
                </span>
                {previewSkipCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                    ⏭ Přeskočeno (prázdné): {previewSkipCount}
                  </span>
                )}
                {notFoundCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700">
                    ❌ Firma nenalezena: {notFoundCount}
                  </span>
                )}
              </div>

              <div className={`rounded-lg border overflow-x-auto text-sm ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className={`text-xs uppercase ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'}`}>
                      <th className="px-3 py-2 text-left w-8">#</th>
                      <th className="px-3 py-2 text-left">IČO</th>
                      <th className="px-3 py-2 text-left">Firma</th>
                      <th className="px-3 py-2 text-left">Nové jméno</th>
                      <th className="px-3 py-2 text-left">Nový email</th>
                      <th className="px-3 py-2 text-left">Nový tel.</th>
                      <th className="px-3 py-2 text-left">Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => (
                      <tr
                        key={row.idx}
                        className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} ${
                          row.action === 'add'
                            ? isDarkMode ? 'bg-green-900/10' : 'bg-green-50/60'
                            : isDarkMode ? 'opacity-50' : 'opacity-60'
                        }`}
                      >
                        <td className={`px-3 py-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{row.idx + 1}</td>
                        <td className="px-3 py-2 font-mono text-xs">{row.ico}</td>
                        <td className="px-3 py-2 font-medium text-xs">
                          {row.found
                            ? row.companyName
                            : <span className="italic text-gray-400">Nenalezena</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.newName || <span className="text-gray-400 italic">–</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.newEmail || <span className="text-gray-400 italic">–</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {row.newPhone || <span className="text-gray-400 italic">–</span>}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          {row.action === 'add' && (
                            <span className="text-green-600 dark:text-green-400 font-semibold">✅ Přidat</span>
                          )}
                          {row.action === 'skip' && (
                            <span className="text-gray-400 font-medium">⏭ Přeskočit</span>
                          )}
                          {row.action === 'notFound' && (
                            <span className="text-red-400 font-medium">❌ Nenalezena</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Chyba ───────────────────────────────────────────────────── */}
          {importError && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300">
              ⚠️ {importError}
            </div>
          )}

          {/* ── Výsledky importu ────────────────────────────────────────── */}
          {importResults && (
            <section>
              <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">✓</span>
                Výsledky importu
              </h3>

              {/* Souhrn */}
              <div className="flex gap-4 mb-4 flex-wrap">
                <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg px-4 py-2 text-sm font-medium text-green-700 dark:text-green-300">
                  ✅ Přidáno: {importResults.summary.ok}
                </div>
                {importResults.summary.notFound > 0 && (
                  <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg px-4 py-2 text-sm font-medium text-orange-700 dark:text-orange-300">
                    🔍 Nenalezeno: {importResults.summary.notFound}
                  </div>
                )}
                {importResults.summary.skipped > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    ⏭ Přeskočeno: {importResults.summary.skipped}
                  </div>
                )}
              </div>

              {/* Detail */}
              <div
                className={`rounded-lg border overflow-hidden text-sm ${
                  isDarkMode ? 'border-gray-600' : 'border-gray-200'
                }`}
              >
                <table className="w-full">
                  <thead>
                    <tr
                      className={`text-xs uppercase ${
                        isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                      }`}
                    >
                      <th className="px-3 py-2 text-left">IČO</th>
                      <th className="px-3 py-2 text-left">Firma</th>
                      <th className="px-3 py-2 text-left">Výsledek</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResults.results.map((row, idx) => (
                      <tr
                        key={idx}
                        className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}
                      >
                        <td className="px-3 py-2 font-mono">{row.ico}</td>
                        <td className="px-3 py-2">{row.companyName || '–'}</td>
                        <td className="px-3 py-2">
                          {row.status === 'ok' ? (
                            <span className="text-green-500 text-xs">
                              ✅ Přidáno (celkem kontaktů: {row.contactsCount})
                            </span>
                          ) : row.status === 'not_found' ? (
                            <span className="text-orange-400 text-xs">❌ {row.reason}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">⏭ {row.reason}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex items-center justify-between gap-3 ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            {importResults ? 'Zavřít' : 'Zrušit'}
          </button>

          {lookupResults && !importResults && (
            <button
              onClick={handleImport}
              disabled={isImporting || previewAddCount === 0}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold text-sm transition"
            >
              {isImporting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Importuji…
                </span>
              ) : (
                `📥 Importovat kontakty (${previewAddCount} záznamů)`
              )}
            </button>
          )}

          {importResults && (
            <button
              onClick={() => {
                // Reset pro nový import
                setIcoInput('');
                setLookupResults(null);
                setNamesInput('');
                setEmailsInput('');
                setPhonesInput('');
                setImportResults(null);
                setImportError('');
              }}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm transition"
            >
              🔄 Nový import
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
